// src/receptionist/components/kit.tsx
//
// The receptionist console in the Oura look (ourteam-frontend#4785).
//
// The screens were built on the older @oura/primitives (Card, PageHeader,
// Button, EmptyState). Those still serve Oura screens the redesign has not
// reached, so they cannot change in place. This file keeps their props and
// draws them with the @oura/ui kit instead: the 32px PageHead, the soft white
// card on the #F5F5F7 ground, pill buttons and CardEmpty. A screen swaps its
// import and nothing else, so no behaviour moves with the restyle.

import { createContext, useContext, type ButtonHTMLAttributes, type ReactNode } from "react";
import { InfoIcon, type LucideIcon } from "lucide-react";
import { CardEmpty, FOCUS_RING, PageHead, SURFACE, TYPE } from "@oura/ui";
import { cn } from "@oura/lib/cn";

/**
 * A screen drawn inside another page, as a Setup step (#4796): its own page
 * head would be a second title, so PageHeader keeps only its actions (Save).
 */
const EmbeddedContext = createContext(false);
export function Embedded({ children }: { children: ReactNode }) {
    return <EmbeddedContext.Provider value>{children}</EmbeddedContext.Provider>;
}

/** The page head: title, the fixed sentence saying what the page is for, and its actions. */
export function PageHeader({
    title,
    subtitle,
    action,
    status,
}: {
    title: string;
    subtitle: string;
    action?: ReactNode;
    /** Today's figures, on their own line under the sentence. */
    status?: ReactNode;
}) {
    const embedded = useContext(EmbeddedContext);
    if (embedded) return action ? <div className="mb-4 flex flex-wrap items-center justify-end gap-2">{action}</div> : null;
    return <PageHead title={title} sentence={subtitle} status={status} actions={action} />;
}

/** The Oura card: white, radius 16, soft shadow, no border. Padding comes from its head and body. */
export function Card({ className, children, id }: { className?: string; children: ReactNode; id?: string }) {
    return <section id={id} className={cn(SURFACE.card, "flex flex-col", className)}>{children}</section>;
}

/** A card's head: a 20px blue icon, the 17px title, the line under it, and a control on the right. */
export function CardHeader({
    title,
    description,
    icon: Icon,
    aside,
    meta,
}: {
    title: ReactNode;
    description?: ReactNode;
    icon?: LucideIcon;
    aside?: ReactNode;
    meta?: ReactNode;
}) {
    return (
        <div className="px-5 pb-3 pt-5">
            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <h2 className="m-0 flex min-w-0 flex-1 basis-40 items-start gap-2.5 text-gray-900">
                    {Icon && <Icon aria-hidden size={20} strokeWidth={2} className="mt-0.5 shrink-0 text-blue-600" />}
                    <span className={cn(TYPE.tt, "min-w-0 break-words")}>{title}</span>
                </h2>
                {aside && <div className="flex shrink-0 items-center gap-2">{aside}</div>}
            </div>
            {description && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>{description}</p>}
            {meta && <div className="mt-2">{meta}</div>}
        </div>
    );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
    return <div className={cn("p-5", className)}>{children}</div>;
}

type Variant = "default" | "outline" | "secondary" | "ghost" | "destructive" | "link";
type Size = "default" | "sm" | "lg" | "icon";

const VARIANT: Record<Variant, string> = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "bg-white text-gray-900 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50",
    secondary: "bg-white text-blue-600 ring-1 ring-inset ring-blue-200 hover:bg-blue-50",
    ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
    destructive: "bg-rose-600 text-white hover:bg-rose-700",
    link: "text-blue-600 hover:underline",
};

const SIZE: Record<Size, string> = {
    default: cn("h-8 px-4", TYPE.tc),
    sm: "h-7 px-3 text-xs",
    lg: "h-10 px-5 text-sm",
    icon: "h-8 w-8",
};

/** The Oura pill button, with the old Button's variants and sizes. */
export function Button({
    variant = "default",
    size = "default",
    className,
    children,
    ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; children: ReactNode }) {
    return (
        <button
            className={cn(
                "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5 disabled:cursor-not-allowed disabled:opacity-40",
                variant === "link" ? "h-auto px-0" : SIZE[size],
                VARIANT[variant],
                FOCUS_RING,
                className,
            )}
            {...rest}
        >
            {children}
        </button>
    );
}

/** An empty or failed state, as Oura draws one inside a card. */
export function EmptyState({
    icon,
    title,
    description,
    action,
}: {
    icon: LucideIcon;
    title: string;
    description?: string;
    action?: ReactNode;
}) {
    return (
        <CardEmpty icon={icon} title={title} action={action} className="py-12">
            {description}
        </CardEmpty>
    );
}

/** The blue info line Oura puts above a page's cards. */
export function InfoNotice({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            role="note"
            className={cn(
                TYPE.tc,
                "flex items-start gap-2.5 rounded-xl bg-[#EAF1FE] px-4 py-3 font-medium text-blue-900 ring-1 ring-inset ring-blue-200",
                className,
            )}
        >
            <InfoIcon aria-hidden size={16} className="mt-px shrink-0 text-blue-600" />
            <span className="min-w-0">{children}</span>
        </div>
    );
}
